"use client";

import { useEffect, useState } from "react";
import { Search, User, ChevronDown } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Layout } from "../../components/Layout";
import { FiltersSheet } from "./components/FiltersSheet";
import { CloseIcon } from "./components/CloseIcon";
import { useFilterOptions } from "../../hooks/useFilterOptions";
import { useDebounce } from "../../hooks/useDebounce";
import { init, swipeBehavior } from "@telegram-apps/sdk";
import { useNavigate } from "react-router-dom";
import type { UserType } from "../../types";

export default function SearchUsers() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [users, setUsers] = useState<UserType[]>([]);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [isIndustriesOpen, setIsIndustriesOpen] = useState(false);
  const [isPositionsOpen, setIsPositionsOpen] = useState(false);

  // Get filter options using custom hook
  const { uniqueCities, uniqueIndustries, uniquePositions } = useFilterOptions({
    users,
  });

  console.log({ selectedIndustries });
  console.log({ selectedPositions });
  console.log({ selectedCities });

  const handleUserClick = (userId: string) => {
    if (isFiltersOpen || isIndustriesOpen || isPositionsOpen) {
      return; // Don't navigate if any filters are open
    }
    navigate(`/users/${userId}/details`);
  };

  const handleApplyFilters = (cities: string[]) => {
    setSelectedCities(cities);
  };

  const handleApplyIndustries = (industries: string[]) => {
    setSelectedIndustries(industries);
  };

  const handleApplyPositions = (positions: string[]) => {
    setSelectedPositions(positions);
  };

  const handleRemoveCity = (cityToRemove: string) => {
    setSelectedCities((prev) => prev.filter((city) => city !== cityToRemove));
  };

  const handleRemoveIndustry = (industryToRemove: string) => {
    setSelectedIndustries((prev) =>
      prev.filter((industry) => industry !== industryToRemove),
    );
  };

  const handleRemovePosition = (positionToRemove: string) => {
    setSelectedPositions((prev) =>
      prev.filter((position) => position !== positionToRemove),
    );
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch("/api/users/list");
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.users) {
        setUsers(data.users);
      } else {
        console.error("Failed to fetch users:", data.error || "Unknown error");
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  useEffect(() => {
    init();
    if (swipeBehavior.mount.isAvailable()) swipeBehavior.mount();

    if (isFiltersOpen || isIndustriesOpen || isPositionsOpen) {
      swipeBehavior.disableVertical();
    } else {
      swipeBehavior.enableVertical();
    }
  }, [isFiltersOpen, isIndustriesOpen, isPositionsOpen]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search query and selected filters
  const filteredUsers = users.filter((user) => {
    const firstName = (user?.first_name ?? "").toLowerCase();
    const lastName = (user?.last_name ?? "").toLowerCase();
    const studyGroup = (user?.study_group ?? "").toLowerCase();
    const employmentSummary = (user?.employment_summary ?? "").toLowerCase();
    const hobbies = (user?.hobbies ?? "").toLowerCase();

    const preparedSearchQuery = searchQuery.toLowerCase();

    const matchesSearch =
      firstName.includes(preparedSearchQuery) ||
      lastName.includes(preparedSearchQuery) ||
      studyGroup.includes(preparedSearchQuery) ||
      employmentSummary.includes(preparedSearchQuery) ||
      hobbies.includes(preparedSearchQuery);


    const matchesCity =
      selectedCities.length === 0 ||
      (user.city && selectedCities.includes(user.city));

    const matchesIndustry =
      selectedIndustries.length === 0 ||
      (user.industries &&
        selectedIndustries.some((selectedIndustry) =>
          user.industries.includes(selectedIndustry),
        ));

    const matchesPosition =
      selectedPositions.length === 0 ||
      (user.positions &&
        selectedPositions.some((selectedPosition) =>
          user.positions.includes(selectedPosition),
        ));

    return matchesSearch && matchesCity && matchesIndustry && matchesPosition;
  });

  useEffect(() => {
    if (!debouncedSearchQuery || debouncedSearchQuery.length < 2) {
      return;
    }
    if (window.ym) {
      window.ym(103622962, "reachGoal", "search_users", {
        searchQuery: debouncedSearchQuery,
      });
    }
  }, [debouncedSearchQuery]);



  return (
    <Layout
      showBackButton
      onBack={() => navigate(-1)}
      title="Поиск выпускников"
      className="flex flex-col h-full"
    >
      <div className="h-full flex flex-col">
        {/* Search header */}
        <div className="pb-4 bg-white sticky top-0 z-[1]">
          <div className="relative mt-4 mb-4">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <Input
              type="search"
              placeholder="Компания, экспертиза, хобби..."
              className="pl-10 bg-gray-100 border-gray-100 text-black"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Filter buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className={`flex items-center gap-1 h-8 px-3 text-sm ${
                selectedCities.length > 0
                  ? "bg-[#D33F44] text-white border-[#D33F44] hover:bg-[#B8353A]"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
              onClick={() => setIsFiltersOpen(true)}
            >
              Город
              <ChevronDown className="h-3 w-3" />
              {selectedCities.length > 0 && (
                <div className="ml-1 h-4 w-4 bg-white/20 rounded-full flex items-center justify-center text-xs font-medium">
                  {selectedCities.length}
                </div>
              )}
            </Button>

            <Button
              variant="outline"
              size="sm"
              className={`flex items-center gap-1 h-8 px-3 text-sm ${
                selectedIndustries.length > 0
                  ? "bg-[#D33F44] text-white border-[#D33F44] hover:bg-[#B8353A]"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
              onClick={() => setIsIndustriesOpen(true)}
            >
              Индустрия
              <ChevronDown className="h-3 w-3" />
              {selectedIndustries.length > 0 && (
                <div className="ml-1 h-4 w-4 bg-white/20 rounded-full flex items-center justify-center text-xs font-medium">
                  {selectedIndustries.length}
                </div>
              )}
            </Button>

            <Button
              variant="outline"
              size="sm"
              className={`flex items-center gap-1 h-8 px-3 text-sm ${
                selectedPositions.length > 0
                  ? "bg-[#D33F44] text-white border-[#D33F44] hover:bg-[#B8353A]"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
              onClick={() => setIsPositionsOpen(true)}
            >
              Позиция
              <ChevronDown className="h-3 w-3" />
              {selectedPositions.length > 0 && (
                <div className="ml-1 h-4 w-4 bg-white/20 rounded-full flex items-center justify-center text-xs font-medium">
                  {selectedPositions.length}
                </div>
              )}
            </Button>
          </div>
        </div>

        <FiltersSheet
          open={isFiltersOpen}
          onDismiss={() => setIsFiltersOpen(false)}
          onApplyOptions={handleApplyFilters}
          availableOptions={uniqueCities}
          selectedOptions={selectedCities}
          title="Город"
          searchPlaceholder="Поиск городов..."
        />

        <FiltersSheet
          open={isIndustriesOpen}
          onDismiss={() => setIsIndustriesOpen(false)}
          onApplyOptions={handleApplyIndustries}
          availableOptions={uniqueIndustries}
          selectedOptions={selectedIndustries}
          title="Фильтр по индустрии"
          searchPlaceholder="Поиск индустрий..."
        />

        <FiltersSheet
          open={isPositionsOpen}
          onDismiss={() => setIsPositionsOpen(false)}
          onApplyOptions={handleApplyPositions}
          availableOptions={uniquePositions}
          selectedOptions={selectedPositions}
          title="Фильтр по функции"
          searchPlaceholder="Поиск..."
        />

        {/* Filter indicator */}
        {(selectedCities.length > 0 ||
          selectedIndustries.length > 0 ||
          selectedPositions.length > 0) && (
          <div className="bg-gray-50 border-b border-gray-100 px-4 py-3 sticky top-[110px] z-[1]">
            <div className="flex flex-wrap gap-2">
              {selectedCities.map((city) => (
                <div
                  key={`city-${city}`}
                  className="flex items-center gap-1 bg-[#D33F44] text-white px-3 py-1.5 rounded-lg text-sm font-medium"
                >
                  <span>{city}</span>
                  <button
                    onClick={() => handleRemoveCity(city)}
                    className="ml-1 hover:bg-[#B8353A] rounded-full p-0.5 transition-colors"
                  >
                    <CloseIcon />
                  </button>
                </div>
              ))}
              {selectedIndustries.map((industry) => (
                <div
                  key={`industry-${industry}`}
                  className="flex items-center gap-1 bg-[#D33F44] text-white px-3 py-1.5 rounded-lg text-sm font-medium"
                >
                  <span>{industry}</span>
                  <button
                    onClick={() => handleRemoveIndustry(industry)}
                    className="ml-1 hover:bg-[#B8353A] rounded-full p-0.5 transition-colors"
                  >
                    <CloseIcon />
                  </button>
                </div>
              ))}
              {selectedPositions.map((position) => (
                <div
                  key={`position-${position}`}
                  className="flex items-center gap-1 bg-[#D33F44] text-white px-3 py-1.5 rounded-lg text-sm font-medium"
                >
                  <span>{position}</span>
                  <button
                    onClick={() => handleRemovePosition(position)}
                    className="ml-1 hover:bg-[#B8353A] rounded-full p-0.5 transition-colors"
                  >
                    <CloseIcon />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users list */}
        <div className="flex-1 overflow-y-auto bg-white">
          {filteredUsers.length > 0 ? (
            <div>
              {filteredUsers.map((user, index) => (
                <div key={user.id}>
                  <div
                    onClick={() => handleUserClick(user.telegram_id)}
                    className="w-full block p-4 hover:bg-gray-100 transition-colors cursor-pointer h-20"
                  >
                    <div className="flex items-center w-full h-full">
                      <Avatar className="h-12 w-12 mr-3 flex-shrink-0">
                        <AvatarImage
                          src={`/api/users/${user.telegram_id}/avatar`}
                          alt={user?.first_name + " " + user?.last_name}
                        />
                        <AvatarFallback className="bg-gray-200 text-gray-600">
                          {user?.first_name?.charAt(0)}
                          {user?.last_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col items-start flex-1 min-w-0">
                        <span className="font-medium text-sm text-black">
                          {user?.first_name} {user?.last_name}
                        </span>
                        <span className="text-xs text-gray-700 font-medium rounded-md w-full line-clamp-3">
                          {user?.employment_summary}
                        </span>
                      </div>
                    </div>
                  </div>
                  {index < filteredUsers.length - 1 && (
                    <div className="h-px bg-gray-100"></div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
              <User className="h-12 w-12 mb-2 opacity-50" />
              <p>Пользователи не найдены</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
